// ============================================================
// BillingView.tsx — Invoicing Stage 1: the firm-wide Billing › Bills
// takeover (sidebar "Billing"; approved mockup screen 6). KPI tiles
// (unbilled WIP, drafts, issued & unpaid, overdue 60+), the aged-debt bar
// (0–30 / 31–60 / 61–90 / 90+), status filters with counts, search,
// fee-earner + "Mine only" filters, and every bill with its status and age.
// The gold left bar marks YOUR bills (the design-language "yours" pattern):
// bills on cases you're involved in — case owner or handling team — whoever
// raised them. "Mine only" uses the same meaning (server flag `mine`).
// All £ / ages come from GET /api/invoices (server-computed, accessible
// cases only). Opening a bill shows BillView in place.
// B3c — a "Credit notes" tab (?kind=credit; "tab" is a reserved place param):
// number, the bill it credits (a link), case, date, amount (a minus figure;
// hidden when its bill's credits are), delivered status — with the same search, fee earner and "Mine only",
// and its own status filter. A row opens the credit note's main view
// (BillView → CreditNoteView); the bill number opens the bill.
// (Batch 2) The tabs follow the WAI-ARIA tabs pattern (arrow keys / Home /
// End, tab ↔ tabpanel linked); ?kind=settlement for someone without "See
// colleagues' billing" opens the Bills tab (the register never mounts).
// ============================================================
import { useLivePermissions } from '../hooks/useLivePermissions'
import { ErrorNote } from '../components/shared/ActionBox'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getCaseBillingApi, listBillsApi } from '../api/casevoApi'
import type { Bill, CaseBilling, FirmBillRow, FirmBills, FirmCreditNoteRow } from '../types/billing.types'
import BillView from '../components/billing/BillView'
import RaiseBillModal from '../components/billing/RaiseBillModal'
import LinkCasePickerModal from '../components/shared/LinkCasePickerModal'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import { SidebarHamburger } from '../components/layout/Sidebar'
import { useCasevoStore } from '../store/useCasevoStore'
import { useFirmChange } from '../utils/liveEvents'
import { goBack, initialPlaceParam, navigateTo, navigateWithin, usePlaceParam } from '../navigation/navigate'
import { useBillingLive } from '../hooks/useBillingLive'
import UserAvatar from '../components/shared/UserAvatar'
import SettlementRegister from '../components/billing/SettlementRegister'
import {
  BIcon, B_PRIMARY, B_SECONDARY, CARD, EYEBROW, INPUT, KpiSkeleton, KpiTile, NOTE, RowsSkeleton, Spinner, StatusPill, settledLabel, TD, TH, TextPill, BILL_TYPE_SHORT, errorText, fmtDate, money, moneyOrHidden, partialMoney, useCanUseBilling,
} from '../components/billing/billingUi'

type Filter = 'ALL' | 'DRAFTS' | 'APPROVAL' | 'ISSUED' | 'PART' | 'OVERDUE' | 'PAID' | 'VOID'
const FILTERS: Filter[] = ['ALL', 'DRAFTS', 'APPROVAL', 'ISSUED', 'PART', 'OVERDUE', 'PAID', 'VOID']
const toFilter = (v: string | null): Filter => (v && (FILTERS as string[]).includes(v) ? v as Filter : 'ALL')
const match = (b: FirmBillRow, f: Filter): boolean => {
  switch (f) {
    case 'DRAFTS': return b.status === 'DRAFT' || b.status === 'PENDING_APPROVAL' || b.status === 'APPROVED'
    case 'APPROVAL': return b.status === 'PENDING_APPROVAL' || (b.status === 'DRAFT' && !!b.submittedAt)
    case 'ISSUED': return (b.status === 'ISSUED' || b.status === 'PART_PAID')
    case 'PART': return b.status === 'PART_PAID'
    case 'OVERDUE': return b.overdue
    case 'PAID': return b.status === 'PAID'
    case 'VOID': return b.status === 'VOID'
    default: return true
  }
}
// B3c — the Credit notes tab's status filter + the search (pure; exported for tests).
type CnFilter = 'ALL' | 'ISSUED' | 'DRAFT' | 'UNSENT' | 'SENT'
const CN_FILTERS: CnFilter[] = ['ALL', 'ISSUED', 'DRAFT', 'UNSENT', 'SENT']
const toCnFilter = (v: string | null): CnFilter => (v && (CN_FILTERS as string[]).includes(v) ? v as CnFilter : 'ALL')
export const matchCreditNote = (c: FirmCreditNoteRow, f: CnFilter): boolean => {
  switch (f) {
    case 'ISSUED': return c.status === 'ISSUED'
    case 'DRAFT': return c.status === 'DRAFT'
    case 'UNSENT': return c.status === 'ISSUED' && !c.delivered
    case 'SENT': return !!c.delivered
    default: return true
  }
}
export const creditNoteSearchHit = (c: FirmCreditNoteRow, needle: string): boolean =>
  !needle || [c.number ?? '', c.billNumber ?? '', c.case.name, c.case.ref, c.createdBy.name ?? ''].some((s) => s.toLowerCase().includes(needle))
// (Batch 2) The Bills | Credit notes | Settlement statements tabs (WAI-ARIA
// tabs pattern, automatic activation). Pure; exported for tests.
type BillingTab = 'bills' | 'credit' | 'settlement'
const TAB_PANEL_ID = 'billing-tabpanel'
const tabId = (t: BillingTab): string => `billing-tab-${t}`
export const tabKeyTarget = <T extends string>(tabs: T[], current: T, key: string): T | null => {
  const i = tabs.indexOf(current)
  if (!tabs.length) return null
  switch (key) {
    case 'ArrowRight': return tabs[(i + 1) % tabs.length]
    case 'ArrowLeft': return tabs[(i - 1 + tabs.length) % tabs.length]
    case 'Home': return tabs[0]
    case 'End': return tabs[tabs.length - 1]
    default: return null
  }
}
const DELIVERED_WORDS: Record<string, string> ={ EMAIL: 'Emailed', POST: 'Posted', HAND: 'Handed over' }
const AGE_COLOUR = (d: number) => (d <= 30 ? 'bg-casevo-brand' : d <= 60 ? 'bg-[#c99a2e]' : d <= 90 ? 'bg-[#e07a3c]' : 'bg-casevo-danger')
const AGE_BUCKET = (d: number) => (d <= 30 ? '0–30' : d <= 60 ? '31–60' : d <= 90 ? '61–90' : '90+')

// PERF: returning to Billing used to show skeletons and re-read the whole list
// (bills + KPIs) every time. The last list THIS signed-in user loaded is kept
// in memory for the session: a revisit paints it at once and re-reads it in
// the background (stale-while-revalidate). Live billing events keep it current
// while on screen. Only ever the same user's own, already-scoped server data.
let billsCache: { userId: string; data: FirmBills } | null = null

const BillingView: React.FC = () => {
  const canBill = useCanUseBilling()
  const userId = useCasevoStore((s) => s.authUser?.id ?? null)
  // Mirrors the server rule (lib/billingAccess.ts) for the scope line only — the
  // list itself is already filtered server-side.
  const seesAllBills = useCasevoStore((s) => s.authUser?.role === 'admin' || s.authUser?.effectivePermissions?.viewAllBilling === true || s.authUser?.effectivePermissions?.seeAllCases === true)
  // B4 — the settlement register needs "See colleagues' billing" (server-enforced too).
  const seesSettlements = useCasevoStore((s) => s.authUser?.role === 'admin' || s.authUser?.effectivePermissions?.seeColleaguesBilling === true)
  const [data, setData] = useState<FirmBills | null>(() => (billsCache && billsCache.userId === userId ? billsCache.data : null))
  const [error, setError] = useState<string | null>(null)
  // The status filter, fee earner and "Mine only" are part of this screen's
  // place (?view=billing&status=…) — a refresh or Back keeps them.
  const [filter, setFilter] = useState<Filter>(() => toFilter(initialPlaceParam('status')))
  const [q, setQ] = useState('')
  const [feeEarner, setFeeEarner] = useState(() => initialPlaceParam('earner') ?? '')
  const [mine, setMine] = useState(() => initialPlaceParam('mine') === '1')
  // B3c — Bills | Credit notes, and the credit notes' own status filter.
  // B4 — a third tab: the firm's settlement statement register.
  const [tabState, setTab] = useState<BillingTab>(() => { const k = initialPlaceParam('kind'); return k === 'credit' ? 'credit' : k === 'settlement' ? 'settlement' : 'bills' })
  // (Batch 2) ?kind=settlement without access (or after losing it) → the
  // Bills tab: the register is never mounted, so no 403 flash.
  const tab: BillingTab = tabState === 'settlement' && !seesSettlements ? 'bills' : tabState
  const [cnFilter, setCnFilter] = useState<CnFilter>(() => toCnFilter(initialPlaceParam('cnStatus')))
  // The open bill (+ the payload we already hold for it, e.g. from the POST
  // that just created it, so BillView renders without a second fetch). A
  // place of its own (?view=billing&billId=…): opening one is a history step,
  // so Back returns to the list; a refresh re-opens it. An id the user can't
  // access just shows BillView's own load error (the server enforces access).
  const [open, setOpen] = useState<{ id: string; initial?: Bill; creditNoteId?: string } | null>(() => {
    const id = initialPlaceParam('billId')
    return id ? { id } : null
  })
  usePlaceParam('billId', open?.id ?? null, (id) => {
    setOpen(id ? { id } : null)
    if (!id && dirty.current) void load()
  })
  usePlaceParam('status', filter === 'ALL' ? null : filter, (v) => setFilter(toFilter(v)))
  usePlaceParam('earner', feeEarner || null, (v) => setFeeEarner(v ?? ''))
  usePlaceParam('mine', mine ? '1' : null, (v) => setMine(v === '1'))
  usePlaceParam('kind', tab === 'bills' ? null : tab, (v) => setTab(v === 'credit' ? 'credit' : v === 'settlement' ? 'settlement' : 'bills'))
  usePlaceParam('cnStatus', cnFilter === 'ALL' ? null : cnFilter, (v) => setCnFilter(toCnFilter(v)))
  const openBill = (b: { id: string; initial?: Bill; creditNoteId?: string }): void => { void navigateWithin(() => setOpen(b)) }
  const [refreshing, setRefreshing] = useState(false)
  // PERF: a bill edit no longer re-fetches the whole firm list (it isn't even
  // visible while a bill is open). Changes patch the row in place and mark the
  // list dirty; ONE background reconcile runs when the user comes back.
  const dirty = useRef(false)
  // "New bill" from the firm Bills page: the shared firm-wide case picker
  // (LinkCasePickerModal — fetchCases, access-scoped server-side) → the case's
  // billing (GET /api/cases/:id/billing, ensureBillingAccess + ensureCaseAccess)
  // → the same RaiseBillModal the case Billing tab uses → BillView.
  const [picking, setPicking] = useState(false)
  const [raiseFor, setRaiseFor] = useState<CaseBilling | null>(null)
  const [pickBusy, setPickBusy] = useState(false)
  const [pickError, setPickError] = useState<string | null>(null)

  // `silent` = a live refresh: no spinner, and a failed background read keeps
  // the rows already on screen rather than replacing them with an error.
  // ★ Live permission change: drop the bills on screen at once if a money
  // right was lost, then re-read (the server decides which £ this user sees).
  const loadRef = useRef<(silent?: boolean) => Promise<void>>(async () => {})
  // (A raise-bill snapshot carries colleagues' line £ too — closed on narrowing.)
  useLivePermissions((narrowed) => { if (narrowed) { setData(null); setRaiseFor(null) } void loadRef.current(true) })
  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true)
    try { setData(await listBillsApi()); setError(null); dirty.current = false } catch (e) { if (!silent) setError(errorText(e, 'Couldn’t load bills.')) } finally { if (!silent) setRefreshing(false) }
  }, [])
  loadRef.current = load
  // Patch one row from the server's fresh bill (its own figures, not recomputed here).
  const patchRow = useCallback((b: Bill) => {
    dirty.current = true
    setData((d) => d && { ...d, bills: d.bills.map((r) => (r.id === b.id ? { ...r, number: b.number, status: b.status, billType: b.billType, submittedAt: b.submittedAt, issuedAt: b.issuedAt, dueAt: b.dueAt, totalPence: b.totals.totalPence, balancePence: b.balancePence, creditedPence: b.creditedPence, writtenOffPence: b.writeOff ? b.writeOff.amountPence : 0, amountPartial: !!b.totalsPartial } : r)) })
  }, [])
  const dropRow = useCallback((id: string) => {
    dirty.current = true
    setData((d) => d && { ...d, bills: d.bills.filter((r) => r.id !== id) })
  }, [])
  // ← Bills: back to the place you were on (usePlaceParam closes the bill and
  // reconciles the list); opened fresh from a link → just close it.
  const closeBill = (): void => { goBack(() => { setOpen(null); if (dirty.current) void load() }) }
  // First visit: a normal load. A revisit (cached list already on screen):
  // revalidate silently — no spinner, no skeleton.
  useEffect(() => { if (canBill) void load(!!(billsCache && billsCache.userId === userId)) }, [load, canBill, userId])
  useEffect(() => { if (data && userId) billsCache = { userId, data } }, [data, userId])

  // LIVE: another user (or this user's other tab) created / issued / paid /
  // credited / wrote off / edited / deleted a bill this user can see — or the
  // socket reconnected, or permissions changed. Re-read the list the normal way
  // (server-scoped bills, KPIs, aged debt, gold flags), silently, so new rows
  // appear, changed rows update in place, deleted drafts disappear and every
  // total is the server's. While a bill is open the list isn't on screen: just
  // mark it stale (one reconcile on the way back, as for local edits).
  const openRef = useRef(open); openRef.current = open
  const dataRef = useRef(data); dataRef.current = data
  const audSeen = useRef(new Set<string>())
  // (D12 live updates) Firm billing changed (payments, write-offs, WIP…).
  useFirmChange(['billing'], () => {
    if (openRef.current) { dirty.current = true; return }
    void load(true)
  }, canBill)
  useBillingLive(() => {
    if (openRef.current) { dirty.current = true; return }
    void load(true)
  }, {
    enabled: canBill,
    // A case's owner / team / firm-wide changed. Restricted users: their list
    // gains or loses that case's bills → refetch on the transition only.
    // Everyone: the gold "yours" flag follows involvement.
    onCaseAudience: ({ caseId, inAudience, involved }) => {
      const rows = (dataRef.current?.bills ?? []).filter((b) => b.case.id === caseId)
      if (rows.some((b) => b.mine !== involved)) return true
      if (seesAllBills) return false
      if (rows.length) return !inAudience
      if (!inAudience) { audSeen.current.delete(caseId); return false }
      if (audSeen.current.has(caseId)) return false
      audSeen.current.add(caseId)
      return true
    },
  })

  const pickCase = async (caseId: string): Promise<void> => {
    setPickBusy(true); setPickError(null)
    try { const b = await getCaseBillingApi(caseId); setPicking(false); setRaiseFor(b) } catch (e) { setPickError(errorText(e, 'Couldn’t load that case’s billing.')) } finally { setPickBusy(false) }
  }

  const earners = useMemo(() => Array.from(new Map([...(data?.bills ?? []), ...(tab === 'credit' ? data?.creditNotes ?? [] : [])].map((b) => [b.createdBy.id, b.createdBy.name ?? '—'])).entries()), [data, tab])
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { ALL: 0, DRAFTS: 0, APPROVAL: 0, ISSUED: 0, PART: 0, OVERDUE: 0, PAID: 0, VOID: 0 }
    for (const b of data?.bills ?? []) (Object.keys(c) as Filter[]).forEach((f) => { if (match(b, f)) c[f]++ })
    return c
  }, [data])
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (data?.bills ?? []).filter((b) => match(b, filter)
      && (!mine || b.mine)
      && (!feeEarner || b.createdBy.id === feeEarner)
      && (!needle || [b.number ?? '', b.case.name, b.case.ref, b.createdBy.name ?? ''].some((s) => s.toLowerCase().includes(needle))))
  }, [data, filter, q, mine, feeEarner])
  const cnCounts = useMemo(() => {
    const c: Record<CnFilter, number> = { ALL: 0, ISSUED: 0, DRAFT: 0, UNSENT: 0, SENT: 0 }
    for (const cn of data?.creditNotes ?? []) CN_FILTERS.forEach((f) => { if (matchCreditNote(cn, f)) c[f]++ })
    return c
  }, [data])
  const cnRows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (data?.creditNotes ?? []).filter((c) => matchCreditNote(c, cnFilter) && (!mine || c.mine) && (!feeEarner || c.createdBy.id === feeEarner) && creditNoteSearchHit(c, needle))
  }, [data, cnFilter, q, mine, feeEarner])

  // Every takeover screen renders its own AppHeader, which portals the app's
  // top bar into the full-width topbar slot (App.tsx). Billing used to render
  // none, so the top bar vanished on this screen. min-h-full (not min-h-screen)
  // on desktop, like the other takeovers: <main> is already viewport-height.
  const goHome = (): void => { void navigateTo({ view: 'caseList' }) }
  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader onWordmarkClick={goHome} leftHamburger={<SidebarHamburger />}
        leftSlot={<BackButton />} />
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">{children}</div>
    </div>
  )
  if (!canBill) return shell(<div className={`${CARD} p-6 ${NOTE}`}>You don’t have access to billing.</div>)

  if (open) return shell(<BillView billId={open.id} initialBill={open.initial} initialCreditNoteId={open.creditNoteId ?? null} backLabel={tab === 'credit' ? 'Credit notes' : 'Bills'} onBack={closeBill} onChanged={patchRow} onDeleted={dropRow} />)

  const tabs = (data ? [['bills', 'Bills', data.bills.length], ['credit', 'Credit notes', data.creditNotes?.length ?? 0], ...(seesSettlements ? [['settlement', 'Settlement statements', null]] : [])] : []) as [BillingTab, string, number | null][]
  const k = data?.kpis
  const aged = k?.aged
  const agedTotal = aged ? aged.d0_30 + aged.d31_60 + aged.d61_90 + aged.d90p : 0
  const pct = (v: number) => (agedTotal > 0 ? (v / agedTotal) * 100 : 0)

  return shell(<>
    <div className="flex items-end gap-3 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-[#9a7b1e] dark:text-casevo-light">Billing</div>
        <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15] m-0 mt-1">Bills</h1>
        <p className={`${NOTE} m-0 mt-1`}>{seesAllBills ? 'Every bill in the firm.' : 'Bills on the cases you can access. Totals cover these bills only.'}</p>
      </div>
      <div className="ml-auto flex gap-2">
        <button type="button" className={B_SECONDARY} disabled={refreshing} aria-busy={refreshing} onClick={() => { void load() }}>{refreshing ? <Spinner /> : <BIcon name="undo" size={13} />}Refresh</button>
        <button type="button" className={B_PRIMARY} onClick={() => { setPickError(null); setPicking(true) }}><BIcon name="plus" size={14} />New bill</button>
      </div>
    </div>
    <LinkCasePickerModal isOpen={picking} onClose={() => { if (!pickBusy) setPicking(false) }} currentCaseId="" excludeCaseIds={[]} title="New bill — choose a case" actionLabel="Select case" mode="select" helperText="Select a case to bill — any case in your firm you can access." busy={pickBusy} error={pickError} onPick={(id) => { void pickCase(id) }} />
    {raiseFor && <RaiseBillModal caseId={raiseFor.case.id} caseName={raiseFor.case.name} caseRef={raiseFor.case.ref} data={raiseFor} onClose={() => setRaiseFor(null)} onCreated={(b) => { setRaiseFor(null); dirty.current = true; openBill({ id: b.id, initial: b }) }} />}
    {error && <ErrorNote className="mt-3">{error}</ErrorNote>}
    {!data ? <div className="mt-[18px] flex flex-col gap-3"><KpiSkeleton /><RowsSkeleton rows={8} /><span className="sr-only" role="status">Loading bills…</span></div> : <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-[18px]">
        <KpiTile label="Unbilled WIP" value={<>{partialMoney(k!.unbilledWipPence, k!.unbilledWipPartial)}</>} sub={`${k!.unbilledWipCases} case${k!.unbilledWipCases === 1 ? '' : 's'} · ${k!.unbilledWipUnits.toLocaleString('en-GB')} units`} />
        <KpiTile label="Drafts" value={<>{partialMoney(k!.draftsPence, k!.draftsPartial)}</>} sub={`${k!.draftsCount} draft${k!.draftsCount === 1 ? '' : 's'}${k!.awaitingApprovalCount ? ` · ${k!.awaitingApprovalCount} awaiting approval` : ''}`} />
        <KpiTile label="Issued & unpaid" value={<>{partialMoney(k!.unpaidPence, k!.unpaidPartial)}</>} sub={`${k!.unpaidCount} bill${k!.unpaidCount === 1 ? '' : 's'} · avg ${k!.unpaidAvgAgeDays} days`} />
        <KpiTile label="Overdue 60+ days" value={<>{partialMoney(k!.over60Pence, k!.over60Partial)}</>} sub={`${k!.over60Count} bill${k!.over60Count === 1 ? '' : 's'}`} tone={k!.over60Count ? 'danger' : undefined} />
      </div>

      <div className={`${CARD} px-[18px] py-4 mt-3`}>
        <div className="flex items-baseline gap-[10px] flex-wrap"><div className={EYEBROW}>Aged debt</div><span className={NOTE}>Issued and unpaid, by days since the bill date</span><b className="ml-auto tabular-nums text-casevo-forest">{partialMoney(agedTotal, k!.unpaidPartial)}</b></div>
        <div className="flex h-3 rounded-[6px] overflow-hidden mt-3 gap-[2px] bg-casevo-border-soft" role="img" aria-label={`Aged debt: ${money(aged!.d0_30)} 0–30 days, ${money(aged!.d31_60)} 31–60, ${money(aged!.d61_90)} 61–90, ${money(aged!.d90p)} over 90`}>
          {agedTotal > 0 && <><i className="block bg-casevo-brand" style={{ width: `${pct(aged!.d0_30)}%` }} /><i className="block bg-[#c99a2e]" style={{ width: `${pct(aged!.d31_60)}%` }} /><i className="block bg-[#e07a3c]" style={{ width: `${pct(aged!.d61_90)}%` }} /><i className="block bg-casevo-danger" style={{ width: `${pct(aged!.d90p)}%` }} /></>}
        </div>
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-[2px] mt-[7px] ${NOTE} tabular-nums`}>
          <span><b className="text-casevo-forest">{money(aged!.d0_30)}</b> · 0–30 days</span><span><b className="text-casevo-forest">{money(aged!.d31_60)}</b> · 31–60</span>
          <span><b className="text-casevo-forest">{money(aged!.d61_90)}</b> · 61–90</span><span><b className="text-casevo-forest">{money(aged!.d90p)}</b> · 90+</span>
        </div>
      </div>

      <div className={`${CARD} mt-3`}>
        {/* B3c — Bills | Credit notes */}
        {/* (Batch 2) A real tab set: ←/→ (wrapping), Home / End move AND
            select; only the selected tab is in the Tab order. */}
        <div role="tablist" aria-label="Bills, credit notes or settlement statements" className="flex gap-[2px] px-4 pt-[12px] border-b border-casevo-border-soft"
          onKeyDown={(e) => {
            const next = tabKeyTarget(tabs.map(([t]) => t), tab, e.key)
            if (!next) return
            e.preventDefault()
            setTab(next)
            document.getElementById(tabId(next))?.focus()
          }}>
          {tabs.map(([t, l, n]) => (
            <button key={t} id={tabId(t)} type="button" role="tab" aria-selected={tab === t} aria-controls={tab === t ? TAB_PANEL_ID : undefined} tabIndex={tab === t ? 0 : -1} onClick={() => setTab(t)}
              className={`px-[12px] py-[8px] -mb-px text-[13.5px] border-b-2 focus:outline-none casevo-focus rounded-t-[6px] ${tab === t ? 'border-casevo-primary text-casevo-forest font-semibold' : 'border-transparent text-casevo-mid hover:text-casevo-forest'}`}>
              {l} {n !== null && <small className="text-[11px] opacity-70 tabular-nums">{n}</small>}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={TAB_PANEL_ID} aria-labelledby={tabId(tab)}>
        {tab === 'settlement' ? <SettlementRegister /> : <>
        <div className="flex items-center gap-[10px] px-4 py-[14px] flex-wrap">
          {tab === 'credit' ? (
            <div role="radiogroup" aria-label="Credit note status" className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface flex-wrap">
              {([['ALL', 'All'], ['ISSUED', 'Issued'], ['DRAFT', 'Drafts'], ['UNSENT', 'Not sent'], ['SENT', 'Sent']] as [CnFilter, string][]).map(([f, l], i) => (
                <button key={f} type="button" role="radio" aria-checked={cnFilter === f} onClick={() => setCnFilter(f)}
                  className={`px-[13px] py-[7px] text-[13px] inline-flex items-center gap-[6px] whitespace-nowrap focus:outline-none casevo-focus ${i ? 'border-l border-casevo-border' : ''} ${cnFilter === f ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/30'}`}>
                  {l} <small className="text-[11px] opacity-70 tabular-nums">{cnCounts[f]}</small>
                </button>
              ))}
            </div>
          ) : (
          <div role="radiogroup" aria-label="Bill status" className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface flex-wrap">
            {([['ALL', 'All'], ['DRAFTS', 'Drafts'], ['APPROVAL', 'Awaiting approval'], ['ISSUED', 'Issued'], ['PART', 'Part-paid'], ['OVERDUE', 'Overdue'], ['PAID', 'Paid'], ['VOID', 'Void']] as [Filter, string][]).map(([f, l], i) => (
              <button key={f} type="button" role="radio" aria-checked={filter === f} onClick={() => setFilter(f)}
                className={`px-[13px] py-[7px] text-[13px] inline-flex items-center gap-[6px] whitespace-nowrap focus:outline-none casevo-focus ${i ? 'border-l border-casevo-border' : ''} ${filter === f ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/30'}`}>
                {l} <small className="text-[11px] opacity-70 tabular-nums">{counts[f]}</small>
              </button>
            ))}
          </div>
          )}
          <label className="ml-auto relative"><span className="sr-only">{tab === 'credit' ? 'Search credit notes' : 'Search bills'}</span>
            <BIcon name="search" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-casevo-text-soft" />
            <input className={`${INPUT} !h-[34px] !w-[230px] pl-8 text-[13px]`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === 'credit' ? 'Search credit notes, bills, cases' : 'Search bills, cases, clients'} /></label>
          <label><span className="sr-only">Fee earner</span>
            <select className={`${INPUT} !h-[34px] !w-auto text-[13px]`} value={feeEarner} onChange={(e) => setFeeEarner(e.target.value)}>
              <option value="">All fee earners</option>{earners.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
            </select></label>
          <label className="inline-flex items-center gap-2 h-[34px] px-3 rounded-[9px] border border-casevo-border bg-casevo-input text-[13px] text-casevo-forest cursor-pointer">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="accent-[#2d6a4f]" />Mine only</label>
        </div>
        {tab === 'credit' ? <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px] min-w-[820px]">
              <thead><tr><th className={`${TH} pl-[18px]`}>Credit note</th><th className={TH}>Credits bill</th><th className={TH}>Case</th><th className={TH}>By</th><th className={TH}>Date</th><th className={`${TH} text-right`}>Amount</th><th className={`${TH} text-right`}>Delivered</th></tr></thead>
              <tbody>
                {cnRows.map((c) => (
                  <tr key={c.id} className="cursor-pointer hover:bg-casevo-input" onClick={() => openBill({ id: c.invoiceId, creditNoteId: c.id })}>
                    <td className={`${TD} relative pl-[18px]`}>
                      {c.mine && <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-1 rounded-r-[3px] bg-casevo-gold" />}
                      <button type="button" className="focus:outline-none casevo-focus rounded-[3px] text-left" onClick={(e) => { e.stopPropagation(); openBill({ id: c.invoiceId, creditNoteId: c.id }) }} aria-label={`Open credit note ${c.number ?? '(draft)'} for ${c.case.name}`}>
                        {c.number ? <span className="font-mono font-semibold text-casevo-forest">{c.number}</span> : <span className="italic text-casevo-text-soft">Draft</span>}
                      </button>
                    </td>
                    <td className={TD}>
                      <button type="button" className="font-mono font-semibold text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-[3px]" onClick={(e) => { e.stopPropagation(); openBill({ id: c.invoiceId }) }} aria-label={`Open bill ${c.billNumber ?? ''}`}>{c.billNumber ?? '—'}</button>
                    </td>
                    <td className={TD}><div className="font-semibold text-casevo-forest">{c.case.name}</div><div className="text-[11.5px] font-mono text-casevo-text-soft">{c.case.ref}</div></td>
                    <td className={TD}>{c.createdBy.name && <UserAvatar userId={c.createdBy.id} fallbackName={c.createdBy.name} />}</td>
                    <td className={`${TD} text-[12.5px] text-casevo-text-soft tabular-nums`}>{fmtDate(c.issuedAt ?? c.createdAt)}</td>
                    <td className={`${TD} text-right tabular-nums`}>{c.amountPence == null ? moneyOrHidden(null) : `−${money(c.amountPence)}`}</td>
                    <td className={`${TD} text-right`}><TextPill>{c.status === 'DRAFT' ? 'Draft' : c.delivered ? `${DELIVERED_WORDS[c.delivered.method ?? ''] ?? 'Sent'} ${fmtDate(c.delivered.at)}` : 'Not sent'}</TextPill></td>
                  </tr>
                ))}
                {cnRows.length === 0 && <tr><td colSpan={7} className={`${TD} ${NOTE} text-center py-8`}>No credit notes match.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex px-[18px] py-[10px] bg-casevo-input border-t border-casevo-border-soft rounded-b-[14px]">
            <span className={NOTE}>Showing {cnRows.length} of {data.creditNotes?.length ?? 0} · gold bar = credit notes on your cases</span><span className={`${NOTE} ml-auto`}>Amounts include VAT</span>
          </div>
        </> : <>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px] min-w-[880px]">
            <thead><tr><th className={`${TH} pl-[18px]`}>Bill no.</th><th className={TH}>Case</th><th className={TH}>Type</th><th className={TH}>By</th><th className={TH}>Date</th><th className={`${TH} text-right`}>Total</th><th className={`${TH} text-right`}>Balance</th><th className={TH}>Age</th><th className={`${TH} text-right`}>Status</th></tr></thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="cursor-pointer hover:bg-casevo-input" onClick={() => openBill({ id: b.id })}>
                  <td className={`${TD} relative pl-[18px]`}>
                    {b.mine && <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-1 rounded-r-[3px] bg-casevo-gold" />}
                    <button type="button" className="focus:outline-none casevo-focus rounded-[3px] text-left" onClick={(e) => { e.stopPropagation(); openBill({ id: b.id }) }} aria-label={`Open ${b.number ?? 'draft bill'} for ${b.case.name}`}>
                      {b.number ? <span className="font-mono font-semibold text-casevo-forest">{b.number}</span> : <span className="italic text-casevo-text-soft">Not numbered</span>}
                    </button>
                  </td>
                  <td className={TD}><div className="font-semibold text-casevo-forest">{b.case.name}</div><div className="text-[11.5px] font-mono text-casevo-text-soft">{b.case.ref}</div></td>
                  <td className={TD}><TextPill>{BILL_TYPE_SHORT[b.billType]}</TextPill></td>
                  <td className={TD}>{b.createdBy.name && <UserAvatar userId={b.createdBy.id} fallbackName={b.createdBy.name} />}</td>
                  <td className={`${TD} text-[12.5px] text-casevo-text-soft tabular-nums`}>{b.issuedAt ? fmtDate(b.issuedAt) : '—'}</td>
                  <td className={`${TD} text-right tabular-nums`}>{partialMoney(b.totalPence, b.amountPartial)}</td>
                  <td className={`${TD} text-right tabular-nums ${b.balancePence === 0 || b.status === 'VOID' ? 'text-casevo-text-soft' : 'font-semibold text-casevo-forest'}`}>{b.status === 'VOID' ? '—' : moneyOrHidden(b.balancePence)}</td>
                  <td className={TD}>{b.ageDays == null ? <span className={NOTE}>—</span> : (
                    <span className="inline-flex items-center gap-[7px] tabular-nums"><i className={`w-2 h-2 rounded-full ${AGE_COLOUR(b.ageDays)}`} aria-hidden="true" />{b.ageDays} d<span className="text-[11px] text-casevo-text-soft">{AGE_BUCKET(b.ageDays)}</span></span>)}</td>
                  <td className={`${TD} text-right`}><StatusPill status={b.status} overdue={b.overdue} awaiting={!!b.submittedAt} settled={settledLabel(b)} /></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className={`${TD} ${NOTE} text-center py-8`}>No bills match.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex px-[18px] py-[10px] bg-casevo-input border-t border-casevo-border-soft rounded-b-[14px]">
          <span className={NOTE}>Showing {rows.length} of {data.bills.length} · gold bar = bills on your cases (owner or handling team)</span><span className={`${NOTE} ml-auto`}>Totals include VAT</span>
        </div>
        </>}
        </>}
        </div>
      </div>
    </>}
  </>)
}

export default BillingView
